/**
 * GuardLocationMap
 *
 * Interactive map showing all guards' locations using Google Maps.
 * Supports marker clustering at low zoom levels for better performance with many guards.
 * Supports trail visualization showing guard patrol paths.
 * Supports patrol area circles showing 50m radius around patrol locations.
 * Manages popup state to ensure only one popup is open at a time.
 *
 * @see Issue 7.1: Implement marker clustering
 * @see Issue 7.2: Add location trail visualization
 * @see Patrol location circles feature
 */

import React, { useCallback, useState, useMemo, useRef } from 'react';
import GoogleMapReact from 'google-map-react';
import { Box, CircularProgress, Typography, Chip } from '@mui/material';
import styles from './GuardLocationMap.module.css';
import {
  GuardLocation,
  MapBounds,
  ClusterOrPoint,
  isCluster,
  PatrolLocation,
  CameraLocation,
  CameraClusterOrPoint,
  isCameraCluster,
} from './types';
import ClusterMarker from './ClusterMarker';
import GuardTrail from './GuardTrail';
import PatrolCircle from './PatrolCircle';
import PatrolCircleInfoWindow from './PatrolCircleInfoWindow';
import PatrolLocationMarker from './PatrolLocationMarker';
import CameraMarker, { Camera } from './CameraMarker';
import CameraClusterMarker from './CameraClusterMarker';
import CameraPopup from './CameraPopup';
import CameraGalleryPopup from './CameraGalleryPopup';
import IncidentMarker from './IncidentMarker';
import type { VmsIncidentMapData } from '../../../../CANvision/types/canvision.types';
import { useMapClusters } from './useMapClusters';
import { useCameraClusters } from './useCameraClusters';
import { useGuardTrail } from './useGuardTrail';
import { usePatrolLocations } from '../../hooks/dispatch/usePatrolLocations';
import { usePatrolLocationsApi } from '../../hooks/dispatch/usePatrolLocationsApi';
import { useGuardPatrolLocations, GuardPatrolLocation } from '../../hooks/dispatch/useGuardPatrolLocations';
import { PatrolLocationScope } from '../../../../../api/dispatch';

// Default map center (Toronto - adjust as needed)
const DEFAULT_CENTER = { lat: 43.6532, lng: -79.3832 };
const DEFAULT_ZOOM = 12;

/** Minimum zoom level to show patrol circles (hide at lower zoom to reduce clutter)
 * Note: Default map zoom is 12, so set threshold at 11 to show circles on load */
const PATROL_CIRCLES_MIN_ZOOM = 11;

interface GuardLocationMapProps {
  /** Guards to display on the map */
  guards: GuardLocation[];
  /** Currently selected guard ID */
  selectedGuardId: number | null;
  /** Callback when a guard marker is clicked */
  onGuardSelect: (guardId: number) => void;
  /** Callback when "View Details" is clicked on a marker */
  onGuardViewDetails?: (guardId: number) => void;
  /** Callback when "View Details" is clicked on a patrol circle */
  onPatrolViewDetails?: (patrolId: number) => void;
  /** Callback when "Site Info" is clicked on a patrol circle */
  onViewSiteDetails?: (addressId: number) => void;
  /** Map center coordinates */
  center?: { lat: number; lng: number };
  /** Map zoom level */
  zoom?: number;
  /** Callback when map center/zoom changes */
  onMapChange?: (center: { lat: number; lng: number }, zoom: number) => void;
  /** Whether data is loading */
  isLoading?: boolean;
  /** Error message to display */
  error?: string | null;
  /** Google Maps API key (defaults to env var) */
  apiKey?: string;
  /** Whether to show patrol area circles (default: true) */
  showPatrolCircles?: boolean;
  /** Callback with patrol location stats for parent display */
  onPatrolLocationsChange?: (stats: { withCoordinates: number; missing: number }) => void;
  /** Patrol location filter scope (default: 'active') */
  patrolScope?: PatrolLocationScope;
  /** Date for patrol location filter (default: today) */
  patrolDate?: Date;
  /** Callback when guard filter state changes (for displaying filter indicator) */
  onGuardFilterChange?: (guardName: string | null, patrolCount: number, isLoading?: boolean) => void;
  /** VMS Cameras to display on the map */
  cameras?: CameraLocation[];
  /** Whether to show cameras on the map (default: true) */
  showCameras?: boolean;
  /** Callback when "View Stream" is clicked on a camera */
  onCameraViewStream?: (camera: CameraLocation) => void;
  /** Callback when "View Alerts" is clicked on a camera */
  onCameraViewAlerts?: (camera: CameraLocation) => void;
  /** Callback when "Dispatch" is clicked on a camera (navigate to scheduler) */
  onCameraDispatch?: (camera: CameraLocation) => void;
  /** VMS Incidents to display on the map */
  incidents?: VmsIncidentMapData[];
  /** Whether to show incidents on the map (default: true) */
  showIncidents?: boolean;
  /** Callback when "View Details" is clicked on an incident marker */
  onIncidentViewDetails?: (incident: VmsIncidentMapData) => void;
  /** Callback when "Dispatch Guard" is clicked on an incident marker */
  onIncidentDispatch?: (incident: VmsIncidentMapData) => void;
  /** Callback after an incident is quick-resolved from the marker */
  onIncidentResolved?: (incidentId: number) => void;
}

const GuardLocationMap: React.FC<GuardLocationMapProps> = ({
  guards,
  selectedGuardId,
  onGuardSelect,
  onGuardViewDetails,
  onPatrolViewDetails,
  onViewSiteDetails,
  center = DEFAULT_CENTER,
  zoom = DEFAULT_ZOOM,
  onMapChange,
  isLoading = false,
  error = null,
  apiKey = import.meta.env.VITE_GOOGLE_API_KEY || '',
  showPatrolCircles = true,
  onPatrolLocationsChange,
  patrolScope = 'active',
  patrolDate,
  onGuardFilterChange,
  cameras = [],
  showCameras = true,
  onCameraViewStream,
  onCameraViewAlerts,
  onCameraDispatch,
  incidents = [],
  showIncidents = true,
  onIncidentViewDetails,
  onIncidentDispatch,
  onIncidentResolved,
}) => {
  // Track which guard's popup is currently open
  const [openPopupGuardId, setOpenPopupGuardId] = useState<number | null>(null);

  // Track which patrol circle's info window is open
  const [selectedPatrolId, setSelectedPatrolId] = useState<number | null>(null);

  // Track which camera's popup is currently open
  const [openCameraPopupId, setOpenCameraPopupId] = useState<number | null>(null);

  // Track gallery popup for non-expandable camera clusters (co-located cameras)
  const [galleryPopupCameras, setGalleryPopupCameras] = useState<CameraLocation[] | null>(null);
  const [galleryPopupCoords, setGalleryPopupCoords] = useState<{ lat: number; lng: number } | null>(null);

  // Track which incident's popup is currently open
  const [openIncidentPopupId, setOpenIncidentPopupId] = useState<number | null>(null);

  // Track map bounds for clustering calculations
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);

  // Store Google Maps instances for trail rendering
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const [mapsApi, setMapsApi] = useState<typeof google.maps | null>(null);

  // Track which guards have trails visible (Set of guard IDs)
  const [visibleTrails, setVisibleTrails] = useState<Set<number>>(new Set());

  // Use clustering hook to group nearby guards at low zoom levels
  const { clusters, supercluster } = useMapClusters(guards, mapBounds, zoom, {
    radius: 60, // Cluster radius in pixels
    maxZoom: 15, // Stop clustering at this zoom
    minPoints: 3, // Minimum guards to form a cluster
  });

  // Use clustering hook to group nearby cameras at low zoom levels
  const { clusters: cameraClusters, supercluster: cameraSupercluster } = useCameraClusters(
    showCameras ? cameras : [],
    mapBounds,
    zoom,
    {
      radius: 60,
      maxZoom: 15,
      minPoints: 3,
    }
  );

  // Get the currently selected guard for trail display
  const selectedGuardForTrail = useMemo(() => {
    const trailGuardIds = Array.from(visibleTrails);
    if (trailGuardIds.length === 0) return null;
    // For now, support single trail - return the first visible trail's guard ID
    return trailGuardIds[0];
  }, [visibleTrails]);

  // Fetch trail data for the selected guard
  const { trail: trailData } = useGuardTrail(selectedGuardForTrail, {
    enabled: selectedGuardForTrail !== null,
    limit: 100,
  });

  // Find the guard data for trail styling
  const trailGuard = useMemo(
    () => guards.find((g) => g.guardId === selectedGuardForTrail),
    [guards, selectedGuardForTrail]
  );

  // Derive patrol locations from guards data for 'active' scope
  const { patrolLocations: derivedPatrolLocations, sitesWithCoordinates, sitesMissingCoordinates } =
    usePatrolLocations(guards);

  // Format date for API (YYYY-MM-DD) with safe date handling
  // Uses local timezone (not UTC) to match the database's date comparison
  const dateString = useMemo(() => {
    try {
      if (!patrolDate) return undefined;
      // Ensure we have a valid Date object
      const date = patrolDate instanceof Date ? patrolDate : new Date(patrolDate);
      if (isNaN(date.getTime())) return undefined;
      // Use local date components (NOT toISOString which converts to UTC)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (err) {
      console.error('[GuardLocationMap] Error formatting date:', err);
      return undefined;
    }
  }, [patrolDate]);

  // Debug: Log API hook parameters
  const apiEnabled = showPatrolCircles && patrolScope !== 'active';
  console.log('[PatrolLocations] Hook params:', {
    dateString,
    patrolScope,
    showPatrolCircles,
    apiEnabled,
  });

  // Fetch patrol locations from API for 'today' and 'all_sites' scopes
  const { locations: apiPatrolLocations, isLoading: apiLoading } = usePatrolLocationsApi({
    date: dateString,
    scope: patrolScope,
    enabled: apiEnabled,
  });

  // FIX: Keep previous patrol locations while loading to prevent flickering
  // and race conditions with guard selection
  const previousPatrolLocationsRef = useRef<PatrolLocation[]>([]);

  // Determine effective patrol locations based on scope
  const effectivePatrolLocations: PatrolLocation[] = useMemo(() => {
    try {
      if (patrolScope === 'active') {
        // Use derived data from guards for real-time active patrols
        const result = Array.isArray(derivedPatrolLocations) ? derivedPatrolLocations : [];
        console.log('[PatrolLocations] Active scope:', result.length, 'locations');
        previousPatrolLocationsRef.current = result;
        return result;
      }

      // Debug logging for API-based data (today/all_sites scopes)
      console.log('[PatrolLocations] API scope:', patrolScope, 'Loading:', apiLoading, 'Raw API data:', apiPatrolLocations);

      // FIX: While API is loading, return previous value to prevent race condition
      // This ensures guard patrol matching doesn't fail against empty array
      if (apiLoading && previousPatrolLocationsRef.current.length > 0) {
        console.log('[PatrolLocations] Returning cached locations while loading:', previousPatrolLocationsRef.current.length);
        return previousPatrolLocationsRef.current;
      }

      // Ensure apiPatrolLocations is an array before mapping
      if (!Array.isArray(apiPatrolLocations) || apiPatrolLocations.length === 0) {
        // If we have no data but also not loading, return empty (initial state)
        return previousPatrolLocationsRef.current.length > 0 ? previousPatrolLocationsRef.current : [];
      }

      // Convert API response to PatrolLocation format with safe access
      const mapped = apiPatrolLocations
        .filter((loc) => loc && typeof loc === 'object' && loc.addressId != null)
        .map((loc): PatrolLocation => ({
          // Use addressId as the unique key for non-active scopes
          patrolId: loc.activePatrol?.patrolId ?? loc.addressId,
          addressId: loc.addressId,
          siteName: loc.siteName ?? 'Unknown Site',
          customerName: loc.customerName ?? '',
          latitude: loc.latitude ?? 0,
          longitude: loc.longitude ?? 0,
          circleStatus: loc.circleStatus ?? 'no_guard',
          guardId: loc.activePatrol?.guardId ?? null,
          guardName: loc.activePatrol?.guardName ?? null,
          guardDistance: loc.activePatrol?.guardDistance ?? null,
          patrolStatus: loc.activePatrol?.patrolStatus ?? 'pending',
        }));

      // Filter out locations with invalid coordinates (0, 0)
      const validLocations = mapped.filter((loc) => loc.latitude !== 0 && loc.longitude !== 0);
      console.log('[PatrolLocations] Mapped:', mapped.length, 'Valid (with coords):', validLocations.length);

      // Cache the result for future loading states
      previousPatrolLocationsRef.current = validLocations;
      return validLocations;
    } catch (err) {
      console.error('[GuardLocationMap] Error processing patrol locations:', err);
      return previousPatrolLocationsRef.current.length > 0 ? previousPatrolLocationsRef.current : [];
    }
  }, [patrolScope, derivedPatrolLocations, apiPatrolLocations, apiLoading]);

  // Notify parent of patrol location stats when they change
  React.useEffect(() => {
    if (patrolScope === 'active') {
      onPatrolLocationsChange?.({
        withCoordinates: sitesWithCoordinates,
        missing: sitesMissingCoordinates,
      });
    } else {
      // For API data, all returned locations have coordinates
      onPatrolLocationsChange?.({
        withCoordinates: effectivePatrolLocations.length,
        missing: 0,
      });
    }
  }, [patrolScope, sitesWithCoordinates, sitesMissingCoordinates, effectivePatrolLocations.length, onPatrolLocationsChange]);

  // Fetch guard-specific patrol locations when a guard is selected
  const {
    guardPatrolLocations,
    guardName: filteredGuardName,
    isLoading: guardPatrolsLoading,
  } = useGuardPatrolLocations({
    guardId: selectedGuardId,
    allPatrolLocations: effectivePatrolLocations,
  });

  // Determine which patrol locations to display (guard-filtered or all)
  const displayPatrolLocations = useMemo(() => {
    if (selectedGuardId && guardPatrolLocations.length > 0) {
      return guardPatrolLocations;
    }
    return effectivePatrolLocations;
  }, [selectedGuardId, guardPatrolLocations, effectivePatrolLocations]);

  // Notify parent of guard filter state changes
  // FIX: Include loading state so badge can show loading indicator
  React.useEffect(() => {
    if (selectedGuardId && filteredGuardName) {
      // Pass loading state so the badge can show appropriate UI
      onGuardFilterChange?.(filteredGuardName, guardPatrolLocations.length, guardPatrolsLoading);
    } else {
      onGuardFilterChange?.(null, 0, false);
    }
  }, [selectedGuardId, filteredGuardName, guardPatrolLocations.length, guardPatrolsLoading, onGuardFilterChange]);

  // Check if we're in guard-filtered mode
  const isGuardFiltered = selectedGuardId !== null && guardPatrolLocations.length > 0;

  // Determine if patrol circles should be visible based on zoom level
  const patrolCirclesVisible = showPatrolCircles && zoom >= PATROL_CIRCLES_MIN_ZOOM;

  // Find the selected patrol for info window (check displayed locations)
  const selectedPatrol = useMemo(
    () => displayPatrolLocations.find((p) => p.patrolId === selectedPatrolId || p.addressId === selectedPatrolId),
    [displayPatrolLocations, selectedPatrolId]
  );

  // Map options - minimal controls for cleaner UI
  const createMapOptions = (maps: typeof google.maps) => ({
    // Enable fullscreen control for expanded map view
    fullscreenControl: true,
    fullscreenControlOptions: {
      position: maps.ControlPosition.RIGHT_TOP,
    },
    // Disable other unnecessary controls
    mapTypeControl: false,
    streetViewControl: false,
    rotateControl: false,
    scaleControl: false,
    panControl: false,
    // Keep zoom control
    zoomControl: true,
    zoomControlOptions: {
      position: maps.ControlPosition.RIGHT_BOTTOM,
    },
    // Simpler gesture handling (no two-finger scroll required)
    gestureHandling: 'greedy',
    // Disable keyboard shortcuts to prevent conflicts
    keyboardShortcuts: false,
    styles: [
      // Subtle map styling to make markers stand out
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }],
      },
    ],
  });

  // Handle map change - update bounds for clustering and notify parent
  const handleMapChange = useCallback(
    ({
      center: newCenter,
      zoom: newZoom,
      bounds,
    }: {
      center: { lat: number; lng: number };
      zoom: number;
      bounds?: { ne: { lat: number; lng: number }; sw: { lat: number; lng: number } };
    }) => {
      onMapChange?.(newCenter, newZoom);

      // Update bounds for clustering calculations
      if (bounds) {
        setMapBounds({
          north: bounds.ne.lat,
          south: bounds.sw.lat,
          east: bounds.ne.lng,
          west: bounds.sw.lng,
        });
      }
    },
    [onMapChange]
  );

  // Handle marker click - toggle popup, close others
  const handleMarkerClick = useCallback(
    (guardId: number) => {
      // Toggle popup: if same guard clicked, close it; otherwise open new one
      setOpenPopupGuardId((prev) => (prev === guardId ? null : guardId));
      onGuardSelect(guardId);
    },
    [onGuardSelect]
  );

  // Handle view details
  const handleViewDetails = useCallback(
    (guardId: number) => {
      setOpenPopupGuardId(null); // Close popup
      onGuardViewDetails?.(guardId);
    },
    [onGuardViewDetails]
  );

  // Handle close popup (click away)
  const handleClosePopup = useCallback(() => {
    setOpenPopupGuardId(null);
  }, []);

  // Handle patrol circle click - open info window
  const handlePatrolCircleClick = useCallback((patrol: PatrolLocation) => {
    // Close any open guard popup
    setOpenPopupGuardId(null);
    // Toggle patrol info window
    setSelectedPatrolId((prev) => (prev === patrol.patrolId ? null : patrol.patrolId));
  }, []);

  // Handle patrol info window close
  const handlePatrolInfoClose = useCallback(() => {
    setSelectedPatrolId(null);
  }, []);

  // Handle patrol view details
  const handlePatrolViewDetails = useCallback(
    (patrolId: number) => {
      setSelectedPatrolId(null); // Close info window
      onPatrolViewDetails?.(patrolId);
    },
    [onPatrolViewDetails]
  );

  // Handle site info view details
  const handleViewSiteDetails = useCallback(
    (addressId: number) => {
      setSelectedPatrolId(null); // Close info window
      onViewSiteDetails?.(addressId);
    },
    [onViewSiteDetails]
  );

  // Handle map click to close any open popup or info window
  const handleMapClick = useCallback(() => {
    setOpenPopupGuardId(null);
    setSelectedPatrolId(null);
    setOpenCameraPopupId(null);
    setGalleryPopupCameras(null);
    setGalleryPopupCoords(null);
    setOpenIncidentPopupId(null);
  }, []);

  // Handle Google Maps API loaded - store instances for trail rendering
  const handleGoogleApiLoaded = useCallback(
    ({ map, maps }: { map: google.maps.Map; maps: typeof google.maps }) => {
      setMapInstance(map);
      setMapsApi(maps);
    },
    []
  );

  // Handle trail toggle for a guard
  const handleToggleTrail = useCallback((guardId: number) => {
    setVisibleTrails((prev) => {
      const next = new Set(prev);
      if (next.has(guardId)) {
        next.delete(guardId);
      } else {
        // For now, only allow one trail at a time for performance
        next.clear();
        next.add(guardId);
      }
      return next;
    });
  }, []);

  // Handle cluster or marker click
  const handleClusterClick = useCallback(
    (feature: ClusterOrPoint) => {
      if (isCluster(feature) && supercluster) {
        // Cluster click: zoom in to expand the cluster
        const clusterId = feature.properties.cluster_id;
        const expansionZoom = Math.min(
          supercluster.getClusterExpansionZoom(clusterId),
          20 // Max zoom cap
        );
        const [lng, lat] = feature.geometry.coordinates;
        onMapChange?.({ lat, lng }, expansionZoom);
        setOpenPopupGuardId(null); // Close any open popup
      } else if (!isCluster(feature)) {
        // Individual marker click: handle like before
        handleMarkerClick(feature.properties.guardId);
      }
    },
    [supercluster, onMapChange, handleMarkerClick]
  );

  // Handle camera marker click - toggle popup
  const handleCameraMarkerClick = useCallback((camera: Camera) => {
    // Close any open guard popup, patrol info, or incident popup
    setOpenPopupGuardId(null);
    setSelectedPatrolId(null);
    setOpenIncidentPopupId(null);
    // Toggle camera popup: if same camera clicked, close it; otherwise open new one
    setOpenCameraPopupId((prev) => (prev === camera.id ? null : camera.id));
  }, []);

  // Handle camera popup close
  const handleCameraPopupClose = useCallback(() => {
    setOpenCameraPopupId(null);
  }, []);

  // Handle camera view stream action
  const handleCameraViewStream = useCallback(
    (camera: CameraLocation) => {
      setOpenCameraPopupId(null); // Close popup
      onCameraViewStream?.(camera);
    },
    [onCameraViewStream]
  );

  // Handle camera view alerts action
  const handleCameraViewAlerts = useCallback(
    (camera: CameraLocation) => {
      setOpenCameraPopupId(null); // Close popup
      onCameraViewAlerts?.(camera);
    },
    [onCameraViewAlerts]
  );

  // Handle camera dispatch action (navigate to scheduler)
  const handleCameraDispatch = useCallback(
    (camera: CameraLocation) => {
      setOpenCameraPopupId(null); // Close popup
      setGalleryPopupCameras(null);
      setGalleryPopupCoords(null);
      onCameraDispatch?.(camera);
    },
    [onCameraDispatch]
  );

  // Handle gallery popup close
  const handleGalleryClose = useCallback(() => {
    setGalleryPopupCameras(null);
    setGalleryPopupCoords(null);
  }, []);

  // Handle camera selection from gallery — close gallery and open stream modal
  const handleGalleryViewStream = useCallback(
    (camera: CameraLocation) => {
      setGalleryPopupCameras(null);
      setGalleryPopupCoords(null);
      onCameraViewStream?.(camera);
    },
    [onCameraViewStream]
  );

  // Handle "View Alerts" from gallery
  const handleGalleryViewAlerts = useCallback(
    (camera: CameraLocation) => {
      setGalleryPopupCameras(null);
      setGalleryPopupCoords(null);
      onCameraViewAlerts?.(camera);
    },
    [onCameraViewAlerts]
  );

  // Handle incident marker click — toggle popup
  const handleIncidentMarkerClick = useCallback((incident: VmsIncidentMapData) => {
    // Close other popups
    setOpenPopupGuardId(null);
    setSelectedPatrolId(null);
    setOpenCameraPopupId(null);
    setGalleryPopupCameras(null);
    setGalleryPopupCoords(null);
    // Toggle incident popup
    setOpenIncidentPopupId((prev) => (prev === incident.id ? null : incident.id));
  }, []);

  // Handle incident popup close
  const handleIncidentPopupClose = useCallback(() => {
    setOpenIncidentPopupId(null);
  }, []);

  // Handle incident view details action
  const handleIncidentViewDetails = useCallback(
    (incident: VmsIncidentMapData) => {
      setOpenIncidentPopupId(null);
      onIncidentViewDetails?.(incident);
    },
    [onIncidentViewDetails]
  );

  // Handle incident dispatch action
  const handleIncidentDispatch = useCallback(
    (incident: VmsIncidentMapData) => {
      setOpenIncidentPopupId(null);
      onIncidentDispatch?.(incident);
    },
    [onIncidentDispatch]
  );

  // Handle incident resolved (quick resolve from marker)
  const handleIncidentResolved = useCallback(
    (incidentId: number) => {
      setOpenIncidentPopupId(null);
      onIncidentResolved?.(incidentId);
    },
    [onIncidentResolved]
  );

  // Handle camera cluster click — always open gallery popup with all cameras in the cluster
  const handleCameraClusterClick = useCallback(
    (clusterId: number, coordinates: [number, number]) => {
      if (!cameraSupercluster) return;

      const [lng, lat] = coordinates;
      setOpenCameraPopupId(null);

      const leaves = cameraSupercluster.getLeaves(clusterId, Infinity);
      const clusterCameras = leaves
        .map((leaf) => cameras.find((c) => c.id === leaf.properties.cameraId))
        .filter((c): c is CameraLocation => c != null);

      if (clusterCameras.length === 1) {
        // Single camera in cluster — open the regular popup
        setOpenCameraPopupId(clusterCameras[0].id);
        setGalleryPopupCameras(null);
        setGalleryPopupCoords(null);
      } else if (clusterCameras.length > 1) {
        setGalleryPopupCameras(clusterCameras);
        setGalleryPopupCoords({ lat, lng });
      }
    },
    [cameraSupercluster, cameras]
  );

  // Find the camera for the currently open popup
  const openCameraPopup = useMemo(
    () => cameras.find((c) => c.id === openCameraPopupId),
    [cameras, openCameraPopupId]
  );

  return (
    <Box className={styles.mapContainer}>
      {/* Loading overlay */}
      {isLoading && (
        <Box className={styles.loadingOverlay}>
          <CircularProgress size={40} />
          <Typography variant="body2" sx={{ mt: 1 }}>
            Loading guard locations...
          </Typography>
        </Box>
      )}

      {/* Error banner */}
      {error && (
        <Box className={styles.errorBanner}>
          <Typography variant="body2">Error: {error}</Typography>
        </Box>
      )}

      {/* Map */}
      <GoogleMapReact
        bootstrapURLKeys={{ key: apiKey }}
        center={center}
        zoom={zoom}
        options={createMapOptions}
        onChange={handleMapChange}
        onClick={handleMapClick}
        yesIamSure={true}
        onGoogleApiLoaded={handleGoogleApiLoaded}
      >
        {clusters.map((feature) => {
          const [lng, lat] = feature.geometry.coordinates;
          // For non-cluster points, find the full guard data for popup display
          const guard = !isCluster(feature)
            ? guards.find((g) => g.guardId === feature.properties.guardId)
            : undefined;

          return (
            <ClusterMarker
              key={
                isCluster(feature)
                  ? `cluster-${feature.properties.cluster_id}`
                  : `guard-${feature.properties.guardId}`
              }
              feature={feature}
              guard={guard}
              lat={lat}
              lng={lng}
              onClick={handleClusterClick}
              isSelected={!isCluster(feature) && selectedGuardId === feature.properties.guardId}
              showPopup={!isCluster(feature) && openPopupGuardId === feature.properties.guardId}
              onViewDetails={handleViewDetails}
              onClosePopup={handleClosePopup}
              showTrail={!isCluster(feature) && visibleTrails.has(feature.properties.guardId)}
              onToggleTrail={handleToggleTrail}
            />
          );
        })}

        {/* Patrol Location Markers - clickable markers at center of each patrol circle */}
        {patrolCirclesVisible &&
          displayPatrolLocations.map((patrol) => {
            // Check if this is a guard patrol location with sequence number
            const guardPatrol = isGuardFiltered
              ? (patrol as GuardPatrolLocation)
              : null;

            return (
              <PatrolLocationMarker
                key={`patrol-marker-${patrol.addressId}`}
                patrol={patrol}
                lat={patrol.latitude}
                lng={patrol.longitude}
                isSelected={selectedPatrolId === patrol.patrolId || selectedPatrolId === patrol.addressId}
                onClick={handlePatrolCircleClick}
                // Pass sequence number and status for guard-filtered view
                sequenceNumber={guardPatrol?.sequenceNumber}
                filteredPatrolStatus={guardPatrol?.patrolStatus}
              />
            );
          })}

        {/* Patrol Circle Info Window - rendered as child of GoogleMapReact for positioning */}
        {selectedPatrol && patrolCirclesVisible && (
          <PatrolCircleInfoWindow
            patrol={selectedPatrol}
            lat={selectedPatrol.latitude}
            lng={selectedPatrol.longitude}
            onViewSiteDetails={handleViewSiteDetails}
            onClose={handlePatrolInfoClose}
          />
        )}

        {/* Camera Markers - VMS cameras on the map */}
        {showCameras &&
          cameraClusters.map((feature) => {
            const [lng, lat] = feature.geometry.coordinates;

            if (isCameraCluster(feature)) {
              // Render cluster marker
              return (
                <CameraClusterMarker
                  key={`camera-cluster-${feature.properties.cluster_id}`}
                  cluster={{
                    id: feature.id as number,
                    geometry: feature.geometry,
                    properties: {
                      cluster: true,
                      cluster_id: feature.properties.cluster_id,
                      point_count: feature.properties.point_count,
                      hasAlerts: (feature.properties as any).hasAlerts ?? 0,
                    },
                  }}
                  lat={lat}
                  lng={lng}
                  onClick={handleCameraClusterClick}
                />
              );
            }

            // Render individual camera marker
            const camera = cameras.find((c) => c.id === feature.properties.cameraId);
            if (!camera) return null;

            return (
              <CameraMarker
                key={`camera-${camera.id}`}
                camera={camera}
                lat={lat}
                lng={lng}
                isSelected={openCameraPopupId === camera.id}
                onClick={handleCameraMarkerClick}
              />
            );
          })}

        {/* Camera Popup - rendered inside GoogleMapReact for positioning */}
        {openCameraPopup && (
          <Box
            // @ts-expect-error - google-map-react positioning props
            lat={openCameraPopup.latitude}
            lng={openCameraPopup.longitude}
            style={{
              position: 'absolute',
              transform: 'translate(-50%, -100%)',
              zIndex: 1000,
            }}
          >
            <CameraPopup
              camera={{
                id: openCameraPopup.id,
                name: openCameraPopup.name,
                display_name: openCameraPopup.display_name,
                site_id: openCameraPopup.site_id,
                site_name: openCameraPopup.site_name,
                status: openCameraPopup.status,
                stream_type: openCameraPopup.stream_type,
                alert_count: openCameraPopup.alert_count,
              }}
              onClose={handleCameraPopupClose}
              onViewStream={() => handleCameraViewStream(openCameraPopup)}
              onViewAlerts={
                openCameraPopup.alert_count > 0
                  ? () => handleCameraViewAlerts(openCameraPopup)
                  : undefined
              }
              onDispatch={
                openCameraPopup.customer_name
                  ? () => handleCameraDispatch(openCameraPopup)
                  : undefined
              }
            />
          </Box>
        )}

        {/* Camera Gallery Popup - for non-expandable clusters (co-located cameras) */}
        {galleryPopupCameras && galleryPopupCameras.length > 0 && galleryPopupCoords && (
          <Box
            // @ts-expect-error - google-map-react positioning props
            lat={galleryPopupCoords.lat}
            lng={galleryPopupCoords.lng}
            style={{
              position: 'absolute',
              transform: 'translate(-50%, -100%)',
              zIndex: 1001,
            }}
          >
            <CameraGalleryPopup
              cameras={galleryPopupCameras}
              onClose={handleGalleryClose}
              onViewStream={handleGalleryViewStream}
              onViewAlerts={handleGalleryViewAlerts}
              onDispatch={
                galleryPopupCameras[0]?.customer_name
                  ? () => handleCameraDispatch(galleryPopupCameras[0])
                  : undefined
              }
            />
          </Box>
        )}

        {/* VMS Incident Markers */}
        {showIncidents &&
          incidents.map((incident) => (
            <IncidentMarker
              key={`incident-${incident.id}`}
              incident={incident}
              lat={incident.latitude}
              lng={incident.longitude}
              showPopup={openIncidentPopupId === incident.id}
              onClick={handleIncidentMarkerClick}
              onViewDetails={handleIncidentViewDetails}
              onDispatch={handleIncidentDispatch}
              onClosePopup={handleIncidentPopupClose}
              onResolved={handleIncidentResolved}
            />
          ))}
      </GoogleMapReact>

      {/* Guard Trail - rendered outside GoogleMapReact children */}
      {selectedGuardForTrail && trailGuard && (
        <GuardTrail
          trail={trailData}
          isVisible={true}
          guardStatus={trailGuard.status}
          map={mapInstance}
          maps={mapsApi}
        />
      )}

      {/* Patrol Area Circles - rendered imperatively like trails */}
      {displayPatrolLocations.map((patrol) => (
        <PatrolCircle
          key={`patrol-circle-${patrol.addressId}`}
          patrol={patrol}
          isVisible={patrolCirclesVisible}
          map={mapInstance}
          maps={mapsApi}
          onClick={handlePatrolCircleClick}
        />
      ))}


      {/* Guard and camera count badges */}
      <Box className={styles.countBadges}>
        <Chip
          label={`${guards.length} guard${guards.length !== 1 ? 's' : ''}`}
          size="small"
          className={styles.guardCountBadge}
        />
        {showCameras && cameras.length > 0 && (
          <Chip
            label={`${cameras.length} camera${cameras.length !== 1 ? 's' : ''}`}
            size="small"
            className={styles.cameraCountBadge}
          />
        )}
        {showIncidents && incidents.length > 0 && (
          <Chip
            label={`${incidents.length} incident${incidents.length !== 1 ? 's' : ''}`}
            size="small"
            sx={{
              backgroundColor: 'rgba(211, 47, 47, 0.9)',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 24,
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default GuardLocationMap;
